import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
//Models
import { IProduct, IProductStates, ProductSearch } from '../models/products/product';
import { ProductCreateResult, ProductResult, ProductListResult, ProductDeleteResult, GenerateProductDataResult } from '../models/products/product-result';
import { ProductNew } from '../models/products/productNew';
import { ProductUploadResult } from '../models/products/productUploadResult';
import {
  applyEnhancementVaraint,
  applyMasterSuite,
  applySettingsVaraint,
} from '../models/products/productSettings';
import { ProductReceival } from '../models/products/actions/receive/productReceival';
import { ITouch } from '../models/products/touch';
import { ProductTouchCreate } from '../models/products/actions/ingest/productTouchCreate';
import { ProductTouch } from '../models/products/actions/ingest/productTouch';
import {
  EAssetType,
  EChannel,
  IAsset,
  IAssetDetailResponse,
  IMasterSuiteProjection,
  IProductProjection,
  latestOutputAssets,
} from '../models/products/asset';
import {
  ITransferExecuteRequest,
  ITransferPrepareResult,
  ITransferSettingsRequest,
} from '../models/products/actions/settings/transferProductSettings';
import { OutputAssetVersionsByIdResponse } from '../models/output-assets/output-asset-versions-by-id';
import { LatestByKindResponse } from '../models/output-assets/latest-by-kind';

//------------------------------------------------------------------------
// Product Service to Manage and hold the current selected Product
//------------------------------------------------------------------------
const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private codeSearch = new Subject<string>();
  codeSearch$ = this.codeSearch.asObservable();
  currentProduct!: IProduct;
  productDetailState: any = {
    id: '',
    filterBy: '',
    activeStep: 'preProduction',
  };
  private isFocusComment = new BehaviorSubject<boolean>(false);
  public currentIsFocusComment = this.isFocusComment.asObservable();
  connectionId = '';
  httpOptionsSignalR = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  };

  private $postAssetState = new BehaviorSubject<EChannel>(EChannel.INTERNAL);
  public postAssetState = this.$postAssetState.asObservable();

  filter: IProductStates = {
    receivalState: [],
    masterSuiteState: [],
    ingestionState: [],
    productionState: [],
    productionReviewState: [],
    enhancementState: [],
    reviewState: [],
    outputsState: [],
    dispatchState: [],
  };
  public latestProductsList: any = {
    batchId: '',
    search: '',
    filter: this.filter,
    preDefinedFilter: undefined,
    pageIndex: 0,
    pageSize: 10,
  };

  constructor(private http: HttpClient) {}

  setPostAssetState(channel: EChannel) {
    this.$postAssetState.next(channel);
  }

  setConnectionId(connectionId: string) {
    this.connectionId = connectionId;
    this.httpOptionsSignalR = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-connection-id': this.connectionId,
      }),
    };
  }

  setStatePD(prop: string, value: any) {
    if (prop === 'id' && value !== this.productDetailState[prop]) {
      this.resetStatePD();
    }
    this.productDetailState[prop] = value;
  }

  resetStatePD() {
    this.productDetailState = {
      filterBy: '',
      activeStep: 'preProduction',
    };
  }

  getStatePD() {
    return this.productDetailState;
  }

  resetLatestProductLists() {
    this.latestProductsList = {
      batchId: '',
      search: '',
      filter: this.filter,
      preDefinedFilter: undefined,
      pageIndex: 0,
      pageSize: 10,
    };
  }

  // Set Current
  setCodeSearch(code: string) {
    this.codeSearch.next(code);
  }

  detailsProduct(productId: string | null): Observable<ProductResult> {
    if (isDevMode()) {
      console.log('[DEV] [ProductsService] loading product details', productId);
    }
    const url = `${environment.apiBase}/products/${productId}`;
    return this.http.get<ProductResult>(url, httpOptions);
  }

  createProduct(newProduct: ProductNew): Observable<ProductCreateResult> {
    const url = `${environment.apiBase}/products`;
    if (isDevMode()) {
      console.log('[DEV] [ProductsService] creating product');
    }
    return this.http.post<ProductCreateResult>(url, newProduct, httpOptions);
  }

  createProducts(file: File, batchId: string | null): Observable<ProductUploadResult> {
    if (isDevMode()) {
      console.log('[DEV] [ProductsService] uploading products file', file.name);
    }
    const formData = new FormData();
    formData.append('file', file);
    const url = `${environment.apiBase}/products/files?batch_id=${batchId}`;
    return this.http.post<ProductUploadResult>(url, formData);
  }

  deleteProduct(product: IProduct): Observable<ProductDeleteResult> {
    if (isDevMode()) {
      console.log('[DEV] [ProductsService] deleting product', product.id);
    }
    const url = `${environment.apiBase}/products/${product.id}`;
    return this.http.delete<ProductDeleteResult>(url, httpOptions);
  }

  listProducts(batchId: string | null): Observable<ProductListResult> {
    const params = batchId != null ? `?batch_id=${batchId}` : '';
    const url = `${environment.apiBase}/products${params}`;
    if (isDevMode()) {
      console.log('[DEV] [ProductsService] loading products');
    }
    return this.http.get<ProductListResult>(url, httpOptions);
  }

  updateProduct(product: IProduct): Observable<ProductResult> {
    const url = `${environment.apiBase}/products/${product.id}`;
    if (isDevMode()) {
      console.log('[DEV] [ProductsService] updating product', product.id);
    }
    return this.http.put<ProductResult>(url, product, httpOptions);
  }

  //------------------------------------------------------------------------
  // Actions
  //------------------------------------------------------------------------

  updateProductReceival(productReceival: ProductReceival): Observable<any> {
    var url = `${environment.apiBase}/product/actions/receive/${productReceival.id}`;
    return this.http.put<ProductReceival>(url, productReceival, httpOptions);
  }

  deleteProductReceival(productReceival: ProductReceival): Observable<any> {
    var url = `${environment.apiBase}/product/actions/receive/${productReceival.id}`;
    return this.http.delete<ProductReceival>(url, httpOptions);
  }

  applyMasterSuiteOnProducts(body: applyMasterSuite): Observable<ProductListResult> {
    const url = `${environment.apiBase}/products/apply/master-suite`;
    return this.http.patch<ProductListResult>(url, body, httpOptions);
  }

  applySettingsVariantOnProducts(body: applySettingsVaraint): Observable<ProductListResult> {
    const url = `${environment.apiBase}/products/apply/settings-variant`;
    return this.http.patch<ProductListResult>(url, body, httpOptions);
  }

  applyEnhancementVariantOnProducts(body: applyEnhancementVaraint): Observable<ProductListResult> {
    const url = `${environment.apiBase}/products/apply/enhancement-variant`;
    return this.http.patch<ProductListResult>(url, body, httpOptions);
  }

  bulkIngestProductTouches(body: ProductTouchCreate[]) {
    var url = `${environment.apiBase}/product/actions/bulk/ingest`;
    return this.http.post<{ productTouches: ProductTouch[] }>(url, body, httpOptions);
  }

  prepareProductSettingsTransfer(body: ITransferSettingsRequest): Observable<ITransferPrepareResult> {
    const url = `${environment.apiBase}/product/actions/settings/transfer/prepare`;
    return this.http.post<ITransferPrepareResult>(url, body, httpOptions);
  }

  executeProductSettingsTransfer(body: ITransferExecuteRequest): Observable<{ products: IProduct[] }> {
    const url = `${environment.apiBase}/product/actions/settings/transfer`;
    return this.http.post<{ products: IProduct[] }>(url, body, httpOptions);
  }

  //------------------------------------------------------------------------
  // Approvals
  //------------------------------------------------------------------------

  clientProductApprovals(id: string, type: string, version: number): Observable<IProduct> {
    var url = `${environment.apiBase}/product/${id}/actions/client/${type}`;
    var versionBody = {
      version: version,
    };
    return this.http.patch<IProduct>(url, versionBody, httpOptions);
  }

  internalProductApprovals(id: string, type: string, version: number): Observable<IProduct> {
    var url = `${environment.apiBase}/product/${id}/actions/internal/${type}`;
    var versionBody = {
      version: version,
    };
    return this.http.patch<IProduct>(url, versionBody, httpOptions);
  }

  patchProductNotes(product: IProduct): Observable<IProduct> {
    var url = `${environment.apiBase}/product/${product.id}/actions/notes`;
    var versionBody = {
      version: product.version,
      notes: product.notes,
    };
    return this.http.patch<IProduct>(url, versionBody, httpOptions);
  }

  //------------------------------------------------------------------------
  // Review Assets
  //------------------------------------------------------------------------

  getViewLatestShootAssets(
    batchId: string,
    productIds: string[],
    pageNumber: number,
    pageSize: number,
  ) {
    const url = `${environment.apiBase}/products/view/latest/shoot-assets`;
    var body = {
      batchId: batchId,
      productIds: productIds,
      pageNumber: pageNumber,
      pageSize: pageSize,
    };
    return this.http.post<{
      pageNumber: number;
      pageSize: number;
      results: IMasterSuiteProjection[];
      total: number;
    }>(url, body);
  }

  getViewLatestPostAssets(
    batchId: string,
    productIds: string[],
    pageNumber: number,
    pageSize: number,
  ) {
    const url = `${environment.apiBase}/products/view/latest/post-assets`;
    var body = {
      batchId: batchId,
      productIds: productIds,
      pageNumber: pageNumber,
      pageSize: pageSize,
    };
    return this.http.post<{
      pageNumber: number;
      pageSize: number;
      results: IAssetDetailResponse[];
      total: number;
    }>(url, body);
  }

  getAssetById(id: string, type: string): Observable<IAsset> {
    const url = `${environment.apiBase}/${
      type === EAssetType.SHOOT_ASSETS ? 'shootAssets' : 'postAssets'
    }/${id}`;
    if (isDevMode()) {
      console.log('[DEV] [ProductsService] loading asset by id', id, type);
    }
    return this.http.get<IAsset>(url, httpOptions);
  }

  getListProduction(id: string) {
    const url = `${environment.apiBase}/products/${id}/shoot-assets/latest`;
    return this.http.get<ITouch[]>(url);
  }

  getListPostProduction(id: string) {
    var url = `${environment.apiBase}/products/${id}/post-assets/latest`;
    return this.http.get<IAsset[]>(url);
  }

  getListAutoPostProduction(id: string) {
    var url = `${environment.apiBase}/products/${id}/auto-post-assets/latest`;
    return this.http.get<IAsset[]>(url);
  }

  getListAllPostProduction(id: string) {
    const url = `${environment.apiBase}/products/${id}/post-assets/all`;
    return this.http.get<ITouch[]>(url);
  }

  getListOutputAssets(id: string, kind?: 'range' | 'ev') {
    const base = `${environment.apiBase}/products/${id}/output-assets/latest`;
    const url = kind ? `${base}?kind=${kind}` : base;
    return this.http.get<latestOutputAssets[]>(url);
  }

  getListOutputAssetsBothKinds(id: string): Observable<LatestByKindResponse> {
    return this.http.get<LatestByKindResponse>(
      `${environment.apiBase}/products/${id}/output-assets/latest-by-kind`,
    );
  }

  getOutputAssetDetails(
    productTouchAssetId: string,
    masterSuiteTouchAssetId: string,
    outputFormatId: string,
  ): Observable<IAssetDetailResponse> {
    const url = `${environment.apiBase}/product-touch-asset/${productTouchAssetId}/master-suite-touch-asset/${masterSuiteTouchAssetId}/output-format/${outputFormatId}/output-assets`;
    return this.http.get<IAssetDetailResponse>(url, httpOptions);
  }

  getOutputAssetById(id: string): Observable<OutputAssetVersionsByIdResponse> {
    const url = `${environment.apiBase}/output-assets/${id}/versions`;
    return this.http.get<OutputAssetVersionsByIdResponse>(url, httpOptions);
  }

  getPostAssetDetails(
    productTouchAssetId: string,
    masterSuiteTouchAssetId: string,
    _: string,
  ): Observable<IAssetDetailResponse> {
    var url = `${environment.apiBase}/product-touch-asset/${productTouchAssetId}/master-suite-touch-asset/${masterSuiteTouchAssetId}/post-assets`;
    return this.http.get<IAssetDetailResponse>(url, httpOptions);
  }

  getAutoPostAssetDetails(
    productTouchAssetId: string,
    masterSuiteTouchAssetId: string,
    _: string,
  ): Observable<IAssetDetailResponse> {
    var url = `${environment.apiBase}/product-touch-asset/${productTouchAssetId}/master-suite-touch-asset/${masterSuiteTouchAssetId}/auto-post-assets`;
    return this.http.get<IAssetDetailResponse>(url, httpOptions);
  }

  getShootAssetDetails(productTouchAssetId: string): Observable<IAssetDetailResponse> {
    const url = `${environment.apiBase}/product-touch-asset/${productTouchAssetId}/shoot-assets`;
    return this.http.get<IAssetDetailResponse>(url, httpOptions);
  }

  reviewShootAsset(id: string, state: string | null) {
    const url = `${environment.apiBase}/shoot-asset/${id}/review/${state}`;
    return this.http.patch<any>(url, null, this.httpOptionsSignalR);
  }

  reviewPostAsset(id: string, isInternal: boolean, state: string | null): Observable<any> {
    const url = `${environment.apiBase}/post-asset/${id}/review${
      isInternal ? '/internal' : '/client'
    }/${state}`;
    return this.http.patch<any>(url, null, this.httpOptionsSignalR);
  }

  reviewOutputAsset(id: string, review: string): Observable<any> {
    const url = `${environment.apiBase}/output-asset/${id}/review/${review}`;
    return this.http.patch<any>(url, null, this.httpOptionsSignalR);
  }

  searchByCode(
    code?: string,
    pageNumber?: number,
    pageSize?: number,
    organizationId?: string,
    isOrganizationMember?: boolean,
    type?: string,
    batchId?: string,
  ) {
    let param = '';
    if (code) {
      param += `search=${code}`;
    }
    if (pageNumber) {
      param += param !== '' ? `&pageNumber=${pageNumber}` : `pageNumber=${pageNumber}`;
    }
    if (pageSize) {
      param += param !== '' ? `&pageSize=${pageSize}` : `pageSize=${pageSize}`;
    }
    if (organizationId) {
      param +=
        param !== '' ? `&OrganizationId=${organizationId}` : `OrganizationId=${organizationId}`;
    }
    if (isOrganizationMember !== undefined) {
      param +=
        param !== ''
          ? `&IsOrganizationMember=${isOrganizationMember}`
          : `IsOrganizationMember=${isOrganizationMember}`;
    }
    if (type) {
      param += param !== '' ? `&Type=${type}` : `Type=${type}`;
    }
    if (batchId) {
      param += param !== '' ? `&BatchId=${batchId}` : `BatchId=${batchId}`;
    }
    const url = `${environment.apiBase}/products/filter?${param}`;
    return this.http.get<{
      pageNumber: number;
      pageSize: number;
      results: ProductSearch[];
      total: number;
    }>(url, httpOptions);
  }

  uploadOutputAsset(postAssetId: string, outputFormatId: string, asset: File): Observable<IAsset> {
    const url = `${environment.apiBase}/post-asset/${postAssetId}/output-format/${outputFormatId}`;
    const formData = new FormData();
    formData.append('file', asset);
    return this.http.post<IAsset>(url, formData);
  }

  syncProductShopifyAssets(data: {
    productIds: string[];
    overwrite: boolean;
    outputFormats: {
      outputFormatId: string;
      type: string;
    }[];
  }): Observable<any> {
    const url = `${environment.apiBase}/organization/products/shopify-sync-assets-queue`;
    return this.http.put(url, data);
  }

  //------------------------------------------------------------------------
  // Download file
  //------------------------------------------------------------------------

  downloadImage(type: string, id: string) {
    const url = `${environment.apiBase}/${type}/${id}/download/raw`;
    return this.http.post(url, null, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  updateIsFocusComment(status: boolean) {
    this.isFocusComment.next(status);
  }

  downloadWeightsAndMeasures(id: string): Observable<Blob> {
    const url = `${environment.apiBase}/product/${id}/export/weights-and-measures`;
    return this.http.get(url, { responseType: 'blob' });
  }

  downloadImageToText(id: string, type: string): Observable<Blob> {
    const url = `${environment.apiBase}/product/${id}/${type}/export/image-to-text`;
    return this.http.get(url, { responseType: 'blob' });
  }

  downloadProductLabelData(id: string): Observable<Blob> {
    const url = `${environment.apiBase}/product/${id}/export/product-label-data`;
    return this.http.get(url, { responseType: 'blob' });
  }

  downloadProductLabelDataList(productIds: string[]): Observable<Blob> {
    const url = `${environment.apiBase}/products/export/product-label-data`;
    return this.http.post(url, productIds, { responseType: 'blob' });
  }

  generateProductData(
    productIds: string[],
    extras?: { readonly target: { readonly formats: readonly string[] } },
  ): Observable<GenerateProductDataResult> {
    const url = `${environment.apiBase}/product-data/generate`;
    const body = extras === undefined ? { productIds } : { productIds, ...extras };
    return this.http.post<GenerateProductDataResult>(url, body);
  }

  downloadImages(urls: string[]) {
    const promises = urls.map((url) => {
      return this.http
        .get(`${environment.s3Base + url}`, {
          responseType: 'blob',
          observe: 'response',
        })
        .toPromise();
    });
    return Promise.all(promises);
  }

  downloadSingleImage(url: string) {
    return this.http.get(`${environment.s3Base + url}`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  transferAssetPlaceholder(
    body: { shootAssetIds?: string[]; postAssetIds?: string[] },
    type: 'shootAssets' | 'postAssets',
  ) {
    const url = `${environment.apiBase}/${type}/transfer-placeholder`;
    return this.http.post(url, body, httpOptions);
  }

  public static getLinkImage(image: string | null) {
    if (image == null) {
      return 'assets/image-placeholder.jpg';
    }
    var res = undefined;
    if (image.includes('https')) {
      res = image;
    }
    if (image.includes('s3://')) {
      res = image.replace('s3://', environment.s3Base);
    }
    if (!res) {
      res = environment.apiBase + image;
    }

    return res;
  }
}
